/**
 * French — the default locale.
 *
 * ## Register
 *
 * Vouvoiement throughout. Buttons and menu entries take the infinitive (« Enregistrer »,
 * « Connexion », « Relancer »); sentences addressed to the reader take the second-person plural or
 * `Veuillez + infinitif`. Titles are in **sentence case**, not the English-style Title Case — that
 * matches every recent component in the estate and is simply correct French.
 *
 * Typography follows the house files: an ordinary space before `:` and `?` rather than a
 * non-breaking one, guillemets « » for quoted UI labels, three ASCII dots for an ellipsis, and no
 * space before `%`.
 *
 * ## Vocabulary decisions worth stating
 *
 * - **« Ressources »**, not « Sites ». The whole point of this product is that servers, sites,
 *   domains and probe targets live in one list with one alerting engine; « Sites » would name a
 *   quarter of it and quietly reintroduce the per-product thinking the architecture forbids.
 * - **« État »** for health, **« Disponibilité »** for uptime, **« Inconnu »** for unknown — the
 *   spellings already established in the estate's other French interfaces.
 * - **« oui Observe » is never translated.** It is the product's name.
 * - Machine vocabularies — capability ids, metric names, event actions, error codes — are shown
 *   verbatim. They are what somebody pastes into a support thread, and a translated identifier is
 *   an identifier that no longer matches the API.
 */
export default {
  app: {
    name: 'oui Observe',
    tagline: 'Supervision unifiée du parc',
  },

  locale: {
    label: 'Langue',
    switch: 'Changer de langue',
  },

  theme: {
    system: 'Thème système',
    light: 'Thème clair',
    dark: 'Thème sombre',
    cycle: 'Changer de thème',
  },

  nav: {
    documentation: 'Documentation de l’API',
    home: 'Tableau de bord',
    homeHint: 'Ce qui demande votre attention',
    sections: 'Sections',
    skipToContent: 'Aller au contenu',
    toggle: 'Afficher ou masquer la navigation',
    assets: 'Ressources',
    assetsHint: 'Tous les types, une seule liste',
    operations: 'Opérations',
    operationsHint: 'En cours et historique',
    activity: 'Activité',
    activityHint: 'Le journal transmis',
    alerts: 'Alertes',
    alertsHint: 'Un seul moteur, tous les types',
    noPrivilegedChannel:
      "Ce tableau de bord ne dispose d'aucun canal privilégié. Tout ce qu'il fait, l'API publique le fait.",
  },

  session: {
    signedIn: 'connecté',
    signOut: 'Déconnexion',
  },

  common: {
    edit: 'Modifier',
    unknown: 'Inconnu',
    none: 'Aucun',
    yes: 'Oui',
    no: 'Non',
    all: 'Tous',
    save: 'Enregistrer',
    cancel: 'Annuler',
    close: 'Fermer',
    confirm: 'Confirmer',
    retry: 'Réessayer',
    reset: 'Réinitialiser',
    clear: 'Effacer',
    search: 'Rechercher',
    filter: 'Filtrer',
    run: 'Lancer',
    loading: 'Chargement...',
    justNow: "À l'instant",
    ago: 'Il y a {duration}',
    in: 'Dans {duration}',
    since: 'depuis {at}',
    on: 'sur',
    of: 'sur',
    andMore: 'et {count} de plus',
    copy: 'Copier',
    copied: 'Copié',
    details: 'Détails',
    verbatim: 'texte intégral',
    selectPlaceholder: '- Sélectionner -',
    add: 'Ajouter',
    select: 'Sélectionner',
    actions: 'Actions',
  },

  units: {
    megabytes: '{value} Mo',
    kilobytes: '{value} Ko',
    milliseconds: 'ms',
    seconds: 's',
    days: 'j',
    percent: '%',
    perSecond: '/s',
    hours: 'h',
  },

  /**
   * Duration parts.
   *
   * Whole patterns rather than bare suffixes, because French puts a space between the number and
   * its unit (`1 min 30 s`) and English does not (`1m 30s`). A shared template would have to pick
   * one and be wrong in the other locale.
   */
  duration: {
    day: '{count} j',
    hour: '{count} h',
    minute: '{count} min',
    second: '{count} s',
    underOneSecond: '< 1 s',
  },

  kind: {
    server: 'Serveur',
    site: 'Site',
    domain: 'Domaine',
    endpoint: 'Point de terminaison',
    unknown: 'Type inconnu',
    serverHint: 'Serveur — un hôte exécutant un agent',
    siteHint: 'Site — une installation avec un agent',
    domainHint: "Domaine — enregistrement et expiration uniquement, sans agent",
    endpointHint: 'Point de terminaison — une cible sondée de l’extérieur, sans agent',
    label: 'Type',
  },

  health: {
    label: 'État',
    unknown: 'Inconnu',
    ok: 'OK',
    warning: 'Avertissement',
    critical: 'Critique',
    unknownHint: "Pas encore vérifié — ce n'est pas la même chose que « sain ».",
    okHint: 'Toutes les vérifications effectuées ont réussi.',
    warningHint: "Quelque chose demande votre attention, mais la ressource répond toujours.",
    criticalHint: 'Quelque chose ne va pas en ce moment.',
  },

  availability: {
    label: 'Disponibilité',
    up: 'En ligne',
    suspect: 'Suspect',
    down: 'Hors ligne',
    recovering: 'Rétablissement',
    unknown: 'Inconnue',
    upHint: 'Joignable depuis toutes les sondes.',
    suspectHint:
      "Une sonde a échoué. Le quorum n'est pas atteint : aucune alerte n'a donc été déclenchée.",
    downHint: 'Un quorum de sondes a conclu à une indisponibilité, sur deux cycles.',
    recoveringHint:
      "Retour à la normale — les sondes répondent de nouveau, mais l'état n'est pas encore stabilisé.",
    unknownHint: "Aucune sonde n'a encore rendu de verdict pour cette ressource.",
  },

  severity: {
    debug: 'débogage',
    info: 'information',
    warning: 'avertissement',
    error: 'erreur',
    critical: 'critique',
    severe: 'gravité supérieure ou égale à 3',
    min: 'Gravité minimale',
  },

  operationState: {
    queued: "En file d'attente",
    running: 'En cours',
    done: 'Terminée',
    error: 'Échec',
    blocked: 'Bloquée',
    unknown: 'État inconnu',
  },

  eventCategory: {
    auth: 'Authentification',
    user: 'Utilisateurs',
    content: 'Contenu',
    extension: 'Extensions',
    form: 'Formulaires',
    commerce: 'Commerce',
    file: 'Fichiers',
    security: 'Sécurité',
    system: 'Système',
    other: 'Autres',
    all: 'Toutes catégories',
  },

  range: {
    label: 'Période',
    '1h': 'Dernière heure',
    '24h': 'Dernières 24 heures',
    '7d': 'Les 7 derniers jours',
    '30d': 'Les 30 derniers jours',
    '13m': 'Les 13 derniers mois',
    custom: 'Période personnalisée',
    selected: 'sur la période sélectionnée',
  },

  auth: {
    signIn: 'Connexion',
    email: 'Adresse e-mail',
    password: 'Mot de passe',
    continue: 'Continuer',
    secondFactor: 'Second facteur',
    signingInAs: 'Connexion en tant que {email}.',
    authenticatorCode: "Code de l'application d'authentification",
    securityKey: 'Clé de sécurité',
    sixDigitCode: 'Code à six chiffres',
    assertion: 'Assertion',
    codeHelp: "Depuis votre application d'authentification.",
    keyHelp: 'Touchez votre clé de sécurité ; votre navigateur remplit ce champ.',
    verify: 'Vérifier',
    useAnotherAccount: 'Utiliser un autre compte',
    mfaMandatory:
      "Un second facteur est exigé pour chaque compte. Cette interface détient les accès à tous les sites gérés par l'agence : c'est la cible la plus intéressante du parc.",
    sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',
    reauthTitle: 'Nouvelle authentification requise',
    reauthDetail:
      "Cette action est destructrice. Confirmez votre identité pour l'autoriser : {label}.",
    reauthConfirm: 'Confirmer mon identité',
    enrolmentRequired:
      "Aucun second facteur n'est enregistré sur ce compte. Contactez votre administrateur pour en enrôler un.",
  },

  home: {
    activityVolume: {
      title: 'Volume d’activité sur 24 heures',
    },
    title: 'Tableau de bord',
    subtitle: "Ce qui demande votre attention, dans l'ordre où il faut se le demander.",
    documentation: 'Documentation',
    health: {
      title: 'État du parc',
      allWell: 'Aucune ressource en défaut.',
    },
    updates: {
      title: 'Mises à jour et sécurité',
      available: 'Mises à jour disponibles',
      vulnerable: 'Extensions vulnérables',
      none: 'Tout est à jour.',
      silent: "{count} ressource(s) ont un agent enrôlé qui n'a encore rien transmis.",
    },
    operations: {
      title: 'Opérations',
      none: 'Aucune opération récente.',
    },
    activity: {
      title: 'Activité récente',
      none: 'Aucun événement reçu.',
      all: "Tout le journal d'activité",
    },
    agents: {
      title: 'Agents à installer',
      intro:
        "Installez l'agent sur un site pour obtenir l'inventaire des extensions, les mises à jour et le journal d'activité. Chaque paquet est construit avec le cœur qui sert cette page : les versions ne peuvent pas diverger.",
      download: 'Télécharger',
      unavailable: 'Les paquets ne sont pas disponibles sur ce déploiement.',
      checksum: 'empreinte {sum}…',
    },
  },
  assets: {
    // A badge on an archived row. Singular, unlike assets.filters.archived,
    // which labels a filter over many.
    archived: 'Archivée',
    title: 'Ressources',
    subtitle:
      "Serveurs, sites, domaines et points de terminaison dans une seule liste. Les alertes, les groupes, les permissions, l'audit et la recherche sont écrits une fois pour toutes contre {code} — c'est pourquoi il n'existe pas de second écran pour les serveurs.",
    label: 'Nom',
    group: 'Groupe',
    tags: 'Étiquettes',
    url: 'Adresse',
    lastSeen: 'Vu pour la dernière fois',
    capabilities: 'Capacités',
    extensions: 'Extensions',
    updates: 'Mises à jour',
    inFlight: 'En cours',
    selectAll: 'Tout sélectionner',
    selectRow: 'Sélectionner cette ressource',
    empty: 'Aucune ressource',
    emptyDetail:
      "Aucune ressource ne correspond à vos critères. Élargissez la recherche ou réinitialisez les filtres.",
    loadError: 'Échec du chargement des ressources',
    countLabel: 'ressources',
    archive: {
      title: 'Archiver',
      action: 'Archiver',
      restore: 'Restaurer',
      confirmOne: 'Archiver « {label} » ?',
      confirmMany: 'Archiver {count} ressources ?',
      whatHappens:
        "La ressource cesse d'être sondée et disparaît de la liste. Tout son historique — événements, mesures, incidents — est conservé, et elle peut être restaurée à tout moment.",
    },
    form: {
      createTitle: 'Nouvelle ressource',
      createSubtitle: 'Ajoutez un site, un serveur ou un domaine à superviser.',
      editTitle: 'Modifier la ressource',
      editSubtitle:
        "Nom, groupe et étiquettes. L'adresse n'est pas modifiable : la changer ferait de cette ressource une autre, dont l'historique appartiendrait à quelqu'un d'autre.",
      createAction: 'Ajouter une ressource',
      kind: 'Type',
      kinds: {
        endpoint: 'Site web',
        server: 'Serveur',
        domain: 'Nom de domaine',
      },
      kindHelp: {
        endpoint:
          "Sondé depuis l'extérieur : disponibilité, temps de réponse, certificat TLS. Installez l'agent ensuite pour obtenir les extensions, les mises à jour et le journal d'activité.",
        server:
          "Une machine. Seul le nom d'hôte est nécessaire ; le système, la mémoire et les processeurs restent inconnus jusqu'à l'enrôlement de son agent.",
        domain: "Enregistrement et expiration uniquement. Rien n'est sondé.",
      },
      noSiteKind:
        "Il n'y a pas de type « site » à choisir : une ressource le devient lorsque son agent s'enrôle et déclare ses capacités. Un manifeste ne peut venir que d'un agent.",
      labelPlaceholder: 'Nom affiché dans les listes et les alertes',
      url: 'Adresse',
      urlHelp: "L'adresse publique du site, celle qui sera sondée à chaque cycle.",
      urlPlaceholder: 'https://exemple.fr/',
      hostname: "Nom d'hôte",
      hostnameHelp: "Le nom par lequel la machine est jointe.",
      hostnamePlaceholder: 'srv-01.exemple.fr',
      domainName: 'Nom de domaine',
      domainNameHelp: 'Sans protocole ni barre oblique.',
      domainNamePlaceholder: 'exemple.fr',
      noGroup: 'Aucun groupe',
      tagsHelp: 'Séparées par des virgules.',
      tagsPlaceholder: 'production, client-x',
    },
    enroll: {
      title: "Relier un agent",
      intro:
        "Un jeton à usage unique autorise l'agent installé sur ce site à s'enrôler. Il n'est valable que quelques minutes.",
      step1: "Installez le paquet oui Observe sur le site.",
      step2: "Ouvrez oui Observe dans l'administration du site.",
      step3: 'Collez-y le jeton ci-dessous, puis validez.',
      mint: 'Générer un jeton',
      onceWarning:
        "Ce jeton n'est affiché qu'une seule fois. Le cœur n'en conserve qu'une empreinte : une fois cette fenêtre fermée, il est irrécupérable et il faut en générer un autre.",
      tokenLabel: "Jeton d'enrôlement",
      coreLabel: 'Cœur',
      expires: 'Expire à {at}.',
      whereToPaste:
        "À coller dans l'écran oui Observe du site. L'adresse du cœur y est déjà renseignée.",
      copy: 'Copier le jeton',
      copied: 'Copié',
    },
    filters: {
      search: 'Rechercher une ressource...',
      kind: 'Type',
      health: 'État',
      availability: 'Disponibilité',
      group: 'Groupe',
      archived: 'Archivées',
      showArchived: 'Afficher les ressources archivées',
      reset: 'Réinitialiser les filtres',
      allGroups: 'Tous les groupes',
      clearCount: 'Effacer les filtres | Effacer un filtre | Effacer {count} filtres',
      declaresHint: "Filtrer sur ce qu'une ressource déclare savoir faire, et non sur ce qu'elle exécute.",
      capabilityAria: 'Filtrer par identifiant de capacité déclarée',
    },
    bulk: {
      selected: 'aucune ressource sélectionnée | une ressource sélectionnée | {count} ressources sélectionnées',
      inCommon: '{count} capacités en commun',
      chooseAction: 'Choisir une action...',
      run: 'Lancer',
      clear: 'Effacer la sélection',
      noCommon:
        "Aucune capacité n'est déclarée par toutes les ressources sélectionnées. Une action groupée n'est proposée que lorsque toutes les cibles peuvent réellement l'exécuter — l'intersection, pas l'union.",
      offered: 'Proposée car toutes les ressources sélectionnées déclarent {list}.',
      queued: '{queued} opérations sur {total} mises en file',
      onAssets: '{action} sur {count} ressources',
    },
    loadingList: 'Chargement des ressources',
    tableCaption: 'Ressources supervisées, avec leur état, leur produit et leurs mises à jour.',
    selectOne: 'Sélectionner {label}',
    declares: 'Déclare',
    declaresHint: 'Nombre de capacités déclarées par cette ressource.',
    inFlightCount: 'aucune opération en cours | une opération en cours | {count} opérations en cours',
    runningCount: 'aucune en cours | une en cours | {count} en cours',
    extUpdateCount: '{count} ext',
    updateStateUnknownHint: "Cette ressource ne rapporte pas l'état de ses mises à jour — ce n'est pas la même chose qu'être à jour.",
  },

  assetDetail: {
    loading: 'Chargement de la ressource',
    loadError: 'Échec du chargement de cette ressource.',
    breadcrumb: 'Ressources',
    operations: 'Opérations',
    operationsSubtitle: 'Les plus récentes en premier, actualisées tant qu’elles sont en cours',
    allOperations: 'Toutes les opérations de cette ressource',
    extensions: 'Extensions',
    manifestFetched: 'manifeste {relative}',
    manifestFetchedHint: 'Date à laquelle le cœur a récupéré le manifeste de cette ressource pour la dernière fois.',
    loadingInventory: "Chargement de l'inventaire",
    inventoryUnavailable: "L'inventaire n'est pas disponible.",
    inventoryError: "Échec du chargement de l'inventaire.",
    recentActivity: 'Activité récente',
    batchSize: 'lot {count}',
    batchSizeHint: "Nombre maximal d'événements par transmission, déclaré par l'agent.",
    searchFullLog: 'Consulter le journal complet',
    declares: 'déclare : {list}',
    capabilitiesUnknown: "Ce que cette ressource sait faire n'est pas encore connu.",
    declaredNotReadable: 'Déclaré, mais pas encore consultable',
    declaredNotReadableSubtitle:
      "La ressource les déclare ; l'API ne propose aucun point d'accès pour les lire",
    declaredNotReadableFooter:
      "Il s'agit d'une lacune de l'API de gestion, pas de la ressource. L'agent déclare bien ces capacités et le point d'accès des opérations les accepte ; il n'existe simplement pas encore de ressource pour en lire l'état.",
    availability: 'Disponibilité',
    uptime: 'Taux de disponibilité',
    currentState: 'État actuel',
    outages: 'Interruptions',
    downtime: 'Indisponibilité',
    averageResponse: 'Temps de réponse moyen',
    lastChecked: 'Dernière vérification',
    noAvailabilityHistory:
      "Aucun historique de disponibilité sur cette période. C'est une valeur inconnue, ni 100%, ni 0%.",
    metrics: 'Mesures',
    product: 'Produit',
    family: 'Famille',
    version: 'Version',
    php: 'PHP',
    agent: 'Agent',
    manifest: 'Manifeste',
    productFooter:
      "La disponibilité d'une mise à jour, la base de données et le serveur web ne sont pas exposés par l'API.",
    backups: 'Sauvegardes',
    backupsDetail:
      "L'agent déclare un fournisseur de sauvegarde. oui Observe n'effectue aucune sauvegarde, et l'API ne propose aucun point d'accès pour en lire l'état — seulement la déclaration ci-dessus.",
    declaredCapabilities: 'Capacités déclarées',
    declaredCapabilitiesSubtitle: '{count} identifiants, texte intégral',
    capabilitiesUnavailable: "Impossible de lire les capacités de cette ressource. Ce n'est pas la même chose qu'une ressource qui n'en déclare aucune.",
    noCapabilities: "Aucune. Le manifeste ne déclare aucune capacité.",
    capabilitiesFooter:
      "Présent signifie pris en charge ; absent signifie non pris en charge. Il n'y a pas de troisième état, et rien ici n'est déduit du produit.",
  },

  capabilities: {
    stepped: 'par étapes',
    mutates: 'modifie',
    reauth: 'ré-authentification',
    notModelled: 'non modélisée ici',
    needs: 'Requiert {fields}',
    cancel: 'Annuler',
    notePlaceholder: 'Ticket, ou motif',
    noteLabel: 'Note',
    manifestStale: 'manifeste obsolète',
    reauthShort: 'ré-auth',
    declaredNotModelled: 'Déclarée, non modélisée par cette version de l’interface',
    newerThanUi: 'Cet agent propose des capacités plus récentes que cette interface. Elles sont transmises avec les paramètres saisis tels quels ; le cœur les valide contre le manifeste.',
    run: 'Lancer…',
    notDeclared: '{count} capacité que cette ressource ne déclare pas | {count} capacités que cette ressource ne déclare pas',
    manifestStaleTitle: 'Le cœur estime que ce manifeste est en retard sur celui de l’agent. Les actions restent affichées ; une opération visant une capacité entre-temps désactivée revient « bloquée ».',
    noteHelp: 'Consigné dans l’entrée d’audit. Le pourquoi, plutôt que le quoi — le quoi figure déjà dans l’enregistrement de l’opération.',
    steppedTitle: 'S’exécute par étapes, avec progression.',
    authorises: 'Autorise {action} une seule fois, pour quelques minutes.',
    confirm: 'Confirmer',
    identityTitle: 'Confirmez que c’est bien vous',
    factorLabel: 'Facteur',
    absentMeans: 'Absente signifie non prise en charge. Il n’y a pas de troisième état — le cœur ne déduit jamais une capacité d’un nom de produit, d’une version ou d’une URL (protocol/capabilities.md §1).',
    actionsTitle: 'Actions',
    noOperationsTitle: 'Cette ressource ne déclare aucune opération.',
    reauthTitle: 'Demande un facteur d’authentification récent et écrit une entrée d’audit (conception §11.4).',
    noneDeclaredHint: 'Rien n’est cassé ici. Un agent peut ne déclarer que core.info, et chaque capacité est désactivable individuellement côté site (conception §11.5). Ce qu’il sait rapporter apparaît dans les panneaux ci-dessous.',
    passwordLabel: 'Mot de passe',
    codeLabel: 'Code',
    reauthExplain: 'Cette action modifie une ressource en production. oui Observe demande un facteur récent et enregistre une entrée d’audit.',
    panelTitle: 'Actions',
    noAgent: 'Aucun agent sur cette ressource',
    noAgentDetail:
      "Cette ressource est surveillée de l'extérieur : oui Observe mesure sa disponibilité et son temps de réponse, sans rien installer dessus. Un agent optionnel ajouterait l'inventaire des extensions, les mises à jour, l'analyse des fichiers et le journal d'activité — mais rien n'est nécessaire pour la supervision de disponibilité ci-dessus.",
    manifestUnavailable: 'Aucun manifeste reçu',
    manifestUnavailableDetail:
      "Ce que cette ressource sait faire est inconnu : aucun manifeste n'a encore été récupéré. Ce n'est pas la même chose qu'un manifeste qui ne déclare rien.",
    noneDeclared: 'Aucune action déclarée',
    noneDeclaredDetail:
      "L'agent a répondu, et son manifeste ne déclare aucune capacité exécutable. C'est une réponse complète, pas une absence de réponse.",
    requiresReauth: 'Demande une nouvelle authentification',
    destructive: 'Action destructrice',
    confirmTitle: 'Confirmer : {label}',
    confirmDetail: 'Cette action sera exécutée sur {asset}.',
    dispatch: 'Lancer',
    dispatched: 'Opération mise en file',
    dispatchFailed: "Échec de la mise en file de l'opération",
    renderedFrom: 'Rendu à partir de {count} capacités déclarées',
    runCapability: 'Lancer la capacité',
    confirmAndReauth: 'Confirmer et se ré-authentifier',
    reauthLabel: '{action} sur {asset}',
    invalidJson: 'Les paramètres ne sont pas du JSON valide.',
    coreRefused: 'Le cœur a refusé l’opération.',
  },

  /**
   * Les mots du registre de capacités.
   *
   * Indexés par le nom camelCase de la constante CAPABILITY, et non par l’identifiant de la
   * capacité : `core.update` contient un point, et un point est ici un séparateur de chemin —
   * `capabilityActions['core.update'].label` serait lu comme quatre objets imbriqués. `unmodelled`
   * désigne l’action générique construite pour un identifiant que cette version ne modélise pas ;
   * son *libellé* est l’identifiant lisible lui-même et n’a délibérément pas d’entrée ici.
   */
  capabilityActions: {
    coreUpdate: {
      label: 'Mettre à jour le cœur',
      summary: 'Met à jour le produit lui-même vers une version précise, étape par étape.',
      confirm:
        'Cette action modifie des fichiers sur le site en production. Elle est irréversible depuis cette interface — une mise à jour annulée n’est pas une mise à jour revenue en arrière.',
      fields: {
        to_version: {
          label: 'Version cible',
          placeholder: '5.2.2',
          help: 'Une version exacte. « latest » n’est jamais envoyé — l’enregistrement de l’opération doit dire ce qui était réellement voulu.',
        },
        backup_first: {
          label: 'Demander au site de sauvegarder d’abord',
          help: 'Pris en compte uniquement si l’agent dispose d’un fournisseur de sauvegarde. Un agent qui n’en a pas l’ignore plutôt que d’échouer.',
        },
        allow_major: {
          label: 'Autoriser le passage à une version majeure',
          help: 'Sans cette option, une cible située au-delà d’une frontière majeure revient bloquée au lieu de se poursuivre.',
        },
      },
    },

    coreChecksum: {
      label: 'Vérifier les fichiers',
      summary: 'Compare les fichiers du produit aux sommes de contrôle officielles de l’éditeur.',
      fields: {
        version: {
          label: 'Version du manifeste de sommes de contrôle',
          placeholder: 'version installée',
          help: 'Laisser vide pour comparer avec la version installée.',
        },
        include_unknown: {
          label: 'Signaler les fichiers absents du manifeste officiel',
        },
      },
    },

    extensionsInstall: {
      label: 'Installer une extension',
      summary: 'Installe une extension absente du site, depuis une URL vérifiée.',
      confirm:
        'Installer du code depuis une URL sur un site en production est l’opération la plus risquée que ce protocole permette. Fournissez une somme de contrôle, sauf raison contraire.',
      fields: {
        source: {
          label: 'Source',
          options: {
            url: 'Télécharger depuis une URL',
            directory: 'Le répertoire du produit lui-même',
          },
        },
        url: {
          label: 'URL du paquet',
          placeholder: 'https://…',
          help: 'Doit être en https. L’agent refuse le texte en clair et refuse les redirections qui sortent de https.',
        },
        checksum: {
          label: 'Somme de contrôle',
          placeholder: 'sha256:…',
          help: 'Lorsqu’elle est fournie, l’agent la vérifie et fait échouer l’opération en cas de différence.',
        },
        slug: {
          label: 'Identifiant attendu',
          help: 'L’agent fait échouer l’opération si ce qu’il a installé ne correspond pas.',
        },
        enable: {
          label: 'Activer après l’installation',
        },
      },
    },

    filesScan: {
      label: 'Analyser les fichiers',
      summary:
        'Détecte les ajouts, les modifications et les suppressions dans l’arborescence de fichiers du site.',
      fields: {
        mode: {
          label: 'Mode',
          options: {
            incremental: 'Incrémental — les changements depuis la dernière analyse',
            full: 'Complet — recalculer toutes les empreintes',
            baseline: 'Référence — enregistrer un nouvel instantané, ne rien signaler',
          },
        },
        max_files: {
          label: 'Plafond de fichiers',
          placeholder: 'valeur par défaut de l’agent',
          help: 'Une limite de sécurité pour une seule opération.',
        },
      },
    },

    cacheClear: {
      label: 'Vider les caches',
      summary: 'Vide les caches du produit. Sans risque, et le premier réflexe après une mise à jour.',
      fields: {
        scopes: {
          label: 'Portées',
          placeholder: 'tout ce que l’agent contrôle',
          help: 'Séparées par des virgules, par exemple page, data, opcode, cdn. Laisser vide pour tout vider.',
        },
      },
    },

    extensionsUpdate: {
      label: 'Mettre à jour',
      summary:
        'Met à jour les extensions sélectionnées vers les versions que l’agent signale comme disponibles.',
      confirm: 'Cette action modifie des fichiers sur le site en production.',
    },

    extensionsToggle: {
      label: 'Activer / désactiver',
      summary: 'Place une extension dans l’état voulu. Idempotent par construction.',
    },

    unmodelled: {
      summary:
        'Déclarée par l’agent de cette ressource, mais non modélisée par cette version de l’interface. Les paramètres sont transmis à l’agent exactement tels qu’ils ont été saisis.',
      confirm:
        'Cette interface ignore ce que fait cette capacité. Le cœur la validera contre le manifeste ; rien d’autre ici ne le fera.',
      fields: {
        __raw: {
          label: 'Paramètres (JSON)',
          help: 'Envoyés tels quels comme objet params de l’opération.',
        },
      },
    },
  },

  /** Les sections en lecture seule du détail d’une ressource, chacune liée à une capacité déclarée. */
  capabilityPanels: {
    core: {
      label: 'Produit',
      summary: 'Version, mise à jour disponible, PHP, base de données et serveur.',
    },
    extensions: {
      label: 'Extensions',
      summary: 'L’inventaire installé, avec l’état des mises à jour et des vulnérabilités.',
    },
    admins: {
      label: 'Comptes privilégiés',
      summary: 'Qui peut modifier cette ressource.',
    },
    backup: {
      label: 'Sauvegardes',
      summary: 'Ce que la ressource dit de son propre dispositif de sauvegarde.',
    },
    activity: {
      label: 'Activité',
      summary: 'Les événements que cette ressource pousse vers le cœur.',
    },
    integrity: {
      label: 'Intégrité des fichiers',
      summary:
        'Les constats issus des analyses de fichiers et de la vérification des sommes de contrôle du cœur.',
    },
  },

  operations: {
    title: 'Opérations',
    subtitle:
      "Ce que oui Observe a demandé aux agents d'exécuter, et ce qu'ils en ont rapporté. Chaque opération porte son propre identifiant, du bouton jusqu'au journal.",
    empty: 'Aucune opération',
    emptyDetail: "Aucune opération ne correspond à ces filtres. Les opérations apparaissent ici dès qu'une action est lancée.",
    loadError: 'Échec du chargement des opérations',
    countLabel: 'opérations',
    capability: 'Capacité',
    state: 'État',
    progress: 'Progression',
    started: 'Démarrée',
    finished: 'Terminée',
    attempt: 'Tentative {count}',
    retry: 'Relancer',
    cancel: "Annuler l'opération",
    blockedReason: 'Motif du blocage',
    result: 'Résultat',
    requestedBy: 'Demandée par',
    noProgress: "L'agent ne rapporte aucune progression pour cette opération.",
    explainQueued: "Acceptée par le cœur, pas encore transmise à l'agent.",
    explainRunning: "L'agent travaille ; le cœur pilote la boucle d'étapes.",
    explainDone: 'Terminée avec succès.',
    explainError: "L'opération a échoué.",
    explainBlocked: 'La ressource a refusé de poursuivre.',
    progressLabel: 'Progression de {capability}',
    elapsed: 'en cours depuis {duration}',
    blockedPolicy:
      "Un blocage est terminal. Le cœur ne relancera pas cette opération, et ce tableau de bord non plus : la relancer avant que la condition sous-jacente ne change produirait exactement la même réponse.",
    blockedUnknown: "La ressource a refusé pour un motif que le cœur ne modélise pas.",
    notRetryable:
      "L'agent indique qu'il est inutile de relancer. Relancer malgré tout produirait le même échec ; la cause se trouve sur la ressource.",
    requestedByLine: 'demandée par {name}',
    reauthenticated: 'identité confirmée',
    /** Motifs de blocage : la clé est le code du cœur, qui n'est jamais traduit. */
    blocked: {
      MAINTENANCE_MODE: 'Le site est hors ligne ou en maintenance et refuse toute modification.',
      CAPABILITY_DISABLED:
        "Cette capacité a été désactivée dans la configuration de l'agent lui-même.",
      INSUFFICIENT_PRIVILEGES:
        "Le compte ou le processus de l'agent ne dispose pas des droits nécessaires.",
      FILESYSTEM_READONLY:
        "L'installation se trouve sur un support en lecture seule ou un déploiement immuable.",
      ANOTHER_OPERATION_RUNNING:
        'Une autre opération détient le verrou sur cette ressource. Réessayez une fois celle-ci terminée.',
      LICENSE_REQUIRED:
        "La cible exige un abonnement ou une clé de licence dont l'agent ne dispose pas.",
      USER_INTERVENTION_REQUIRED: "Quelqu'un doit d'abord intervenir sur le site lui-même.",
      PRECONDITION_FAILED: "Une condition préalable de cette capacité n'est pas remplie.",
    },
    filters: {
      state: 'État',
      capability: 'Capacité',
      asset: 'Ressource',
      active: 'En cours uniquement',
    },
  },

  activity: {
    title: 'Activité',
    subtitle:
      "Les événements transmis par les ressources supervisées vers le cœur. Transmis, et non collectés : un attaquant qui contrôle un site peut effacer ses journaux locaux, mais pas ceux qui l'ont déjà quitté.",
    volume: 'Volume',
    volumeSubtitle: "Depuis l'agrégat horaire continu, et non depuis la table des événements",
    loadingChart: 'Chargement du graphique',
    searching: 'Recherche en cours',
    empty: 'Aucun événement',
    emptyDetail:
      "Aucun événement ne correspond à ces critères sur cette période. Élargissez la période ou retirez des filtres.",
    countLabel: 'événements',
    occurredAt: 'Horodatage',
    asset: 'Ressource',
    category: 'Catégorie',
    action: 'Action',
    severityCol: 'Gravité',
    actor: 'Auteur',
    target: 'Cible',
    sourceIp: 'IP source',
    payload: 'Charge utile',
    emptyDetailNoPush:
      "Aucun événement ne correspond à ces critères. Une ressource qui n'a jamais déclaré logs.push n'en transmet aucun : ce silence est une lacune de capacité, et non une journée sans activité. Élargissez la période ou retirez un filtre.",
    tableCaption: "Événements du journal d'activité, les plus récents en premier.",
    eventId: "Identifiant de l'événement",
    operation: 'Opération',
    userAgent: 'Agent utilisateur',
    filters: {
      search: 'Rechercher dans le journal...',
      actor: 'Auteur',
      action: 'Action',
      category: 'Catégorie',
      severityMin: 'Gravité minimale',
      groupBy: 'Grouper par',
      groupNone: 'Aucun regroupement',
      groupCategory: 'Catégorie',
      groupAction: 'Action',
      groupAsset: 'Ressource',
      reset: 'Réinitialiser les filtres',
      actorAria: 'Filtrer par auteur',
      actionAria: 'Filtrer par action',
      severityAny: 'Toutes gravités',
      severityAndAbove: '{severity} et au-dessus',
      severityCriticalOnly: 'critique uniquement',
      groupChartCategory: 'Grouper le graphique par catégorie',
      groupChartSeverity: 'Grouper le graphique par gravité',
      groupChartAsset: 'Grouper le graphique par ressource',
      groupChartTotal: 'Une seule série, le total',
      notDeclared:
        "L'agent de cette ressource ne déclare pas qu'il émet cette catégorie.",
      coverageDeclares: 'Cette ressource déclare',
      coverageDetail:
        "Les catégories absentes de cette liste sont signalées ; un résultat vide pour l'une d'elles est une lacune de couverture, et non une journée calme.",
    },
  },

  alerts: {
    title: 'Alertes',
    subtitle:
      "Un seul moteur de règles pour tous les types de ressources. Une règle est évaluée sur des faits ; une règle qui exige un fait que personne ne rapporte ne se déclenchera jamais, et le dit.",
    empty: 'Aucune règle',
    emptyDetail: "Aucune règle d'alerte n'est encore définie. Créez-en une pour être prévenu.",
    loadError: 'Échec du chargement des règles',
    newRule: 'Nouvelle règle',
    editRule: 'Modifier la règle',
    deleteRule: 'Supprimer la règle',
    ruleLabel: 'Nom de la règle',
    scope: 'Portée',
    scopeAll: 'Tout le parc',
    scopeGroup: 'Un groupe',
    scopeAsset: 'Une ressource',
    condition: 'Condition',
    severityField: 'Gravité',
    channels: 'Canaux',
    throttle: 'Limitation',
    throttleHelp: "Délai minimal entre deux notifications pour cette règle, en secondes.",
    enabled: 'Activée',
    disabled: 'Désactivée',
    requiredFacts: 'Faits requis',
    requiredFactsHelp:
      "Cette règle a besoin de ces faits. Une ressource qui n'en rapporte aucun ne déclenchera jamais cette règle.",
    saved: 'Règle enregistrée',
    deleted: 'Règle supprimée',
    saveFailed: "Échec de l'enregistrement de la règle",
    scopeKind: "Toutes les ressources d'un type",
    scopeTag: 'Toutes les ressources portant une étiquette',
    scopeWhich: 'Cible',
    labelPlaceholder: 'Pression disque sur les serveurs de production',
    appliesTo: "S'applique à",
    appliesToHelp:
      "Une seule forme de règle couvre tous les types de ressources — c'est tout l'intérêt d'alerter sur la ressource plutôt que sur un type particulier.",
    chooseGroup: 'Choisir un groupe',
    chooseAsset: 'Choisir une ressource',
    chooseKind: 'Choisir un type',
    tagPlaceholder: 'étiquette',
    conditionHelp:
      "Stockée sous forme d'arbre déclaratif : comparable dans une entrée d'audit, et affichable sous forme de formulaire.",
    severityHelp:
      'Un libellé ici, un entier sur les événements. Les deux sont voulus ; le cœur fait la correspondance.',
    throttleRationale:
      "Une alerte qui se répète chaque minute est une alerte que l'on finit par classer dans un dossier.",
    channelsRequired: 'Veuillez en choisir au moins un.',
    saveChanges: 'Enregistrer les modifications',
    createRule: 'Créer la règle',
    throttleEvery: 'À chaque déclenchement',
    throttle15m: 'Au plus toutes les 15 minutes',
    throttleHourly: 'Au plus une fois par heure',
    throttle6h: 'Au plus toutes les 6 heures',
    throttleDaily: 'Au plus une fois par jour',
    channel: {
      email: 'E-mail',
      webpush: 'Notification push',
      webhook: 'Webhook',
      slack: 'Slack',
      'google-chat': 'Google Chat',
      'oui-send': 'oui Send',
      intervention: 'Intervention (ticket)',
    },
    conditionBuilder: {
      all: 'Toutes les conditions',
      any: "Au moins une condition",
      not: 'Négation',
      always: 'Toujours',
      fact: 'Fait',
      operator: 'Opérateur',
      value: 'Valeur',
      addCondition: 'Ajouter une condition',
      addGroup: 'Ajouter un groupe',
      remove: 'Retirer',
      fireWhen: 'Déclencher lorsque',
      allMatch: 'toutes les conditions sont remplies',
      anyMatch: 'au moins une condition est remplie',
      removeCondition: 'Retirer cette condition',
      valueFor: 'Valeur pour {fact}',
      commaSeparated: 'séparées par des virgules',
      noValueNeeded: "porte sur ce que nous savons : aucune valeur n'est nécessaire",
      /** Les clés sont les opérateurs du cœur, qui ne sont jamais traduits. */
      operators: {
        eq: 'est',
        ne: "n'est pas",
        lt: 'est inférieur à',
        lte: 'est au plus',
        gt: 'est supérieur à',
        gte: 'est au moins',
        in: 'fait partie de',
        not_in: 'ne fait pas partie de',
        contains: 'contient',
        starts_with: 'commence par',
        ends_with: 'se termine par',
        exists: 'est connu',
        missing: 'est inconnu',
      },
      /**
       * Libellés des faits suggérés. Le fait lui-même — `metric.disk.pct` — reste tel quel : c'est
       * un identifiant, et un identifiant traduit ne correspond plus à l'API.
       */
      facts: {
        availabilityState: 'État de disponibilité',
        availabilityStateHelp:
          'Seuls « down » et « recovering » sont atteints après quorum sur deux cycles (conception §9.3).',
        healthState: 'État',
        responseTime: 'Temps de réponse',
        cpu: 'Processeur',
        memory: 'Mémoire',
        disk: 'Disque',
        tlsDaysLeft: 'Jours restants sur le certificat TLS',
        coreUpdateAvailable: 'Mise à jour du produit disponible',
        coreUpdateSeverity: 'Gravité de la mise à jour du produit',
        coreUpdateSeverityHelp: 'Valeur ouverte ; « security » est celle qui mérite une alerte.',
        extensionsOutdated: 'Extensions obsolètes',
        extensionsVulnerable: 'Extensions vulnérables',
        eventCategory: "Catégorie d'événement",
        eventAction: "Action d'événement",
        eventSeverity: "Gravité de l'événement",
        availabilityRecovered: 'Ressource rétablie',
      availabilityRecoveredHelp:
        "Vrai au moment précis où la ressource répond de nouveau après une interruption. Se déclenche une seule fois, à la reprise — contrairement à « État de disponibilité », qui reste vrai pendant toute la panne.",
      availabilityOutageStarted: 'Début d’interruption',
      availabilityOutageStartedHelp:
        "Vrai au moment précis où le quorum conclut à une indisponibilité. Une seule fois par incident.",
      availabilityChanged: 'Changement d’état',
      availabilityChangedHelp: "Vrai à chaque transition d'état de disponibilité, dans les deux sens.",
      availabilityNotifies: 'Transition à signaler',
      availabilityNotifiesHelp:
        "La conclusion du quorum : cette transition mérite une notification. Filtre les oscillations qu'une sonde isolée provoque.",
      agentSilentHours: "Heures depuis la dernière transmission de l'agent",
        agentSilentHoursHelp:
          "Une ressource qui a transmis quotidiennement pendant des mois puis s'est tue peut avoir eu son agent désactivé (conception §13).",
        backupConfigured: 'Sauvegarde configurée',
        superUserCount: 'Nombre de super-utilisateurs',
      },
      joinAll: 'et',
      joinAny: 'ou',
      negated: 'non ({condition})',
    },
    quorumIntro: "Un seul moteur de règles et un seul chemin de notification, pour tous les types de ressources. Une sonde en échec ne déclenche jamais d'alerte à elle seule : une ressource n'est hors ligne que lorsqu'un quorum de sondes converge, sur deux cycles consécutifs.",
    factsBanner: "Aucune règle ne dépend de faits qui doivent d'abord être rapportés. | Une règle dépend de faits qui doivent être rapportés avant qu'elle puisse se déclencher. | {count} règles dépendent de faits qui doivent être rapportés avant qu'elles puissent se déclencher.",
    factsBannerDetail: "Une règle dont personne ne produit les faits n'est pas une règle silencieuse, c'est une règle cassée ; et le jour où quelqu'un s'en aperçoit est en général le jour où elle aurait dû se déclencher.",
    loadingRules: 'Chargement des règles',
    createFirstRule: 'Créer la première règle',
    tableCaption: "Règles d'alerte, avec leur portée, leur condition et leurs canaux.",
    toggleRule: 'Activer ou désactiver la règle « {label} »',
    needsFacts: 'nécessite {facts}',
    scopeValue: '{type} : {value}',
    ruleCount: 'aucune règle | une règle | {count} règles',
    enabledCount: 'aucune activée | une activée | {count} activées',
    columns: {
      on: 'Active',
      rule: 'Règle',
      created: 'Créée',
    },
    scopeType: {
      group: 'Groupe',
      asset: 'Ressource',
      kind: 'Type',
      tag: 'Étiquette',
    },
  },

  extensions: {
    title: 'Extensions',
    name: 'Nom',
    type: 'Type',
    version: 'Version',
    available: 'Disponible',
    status: 'État',
    empty: 'Aucune extension',
    emptyDetail: "L'agent a répondu, et l'inventaire est vide.",
    updateAvailable: 'Mise à jour disponible',
    securityUpdate: 'Mise à jour de sécurité',
    vulnerable: 'Vulnérable',
    vulnerableHint: 'Vulnérabilité possible',
    upToDate: 'À jour',
    unknownState: "Inconnu — l'agent n'a pas indiqué si une mise à jour existe",
    enabled: 'Activée',
    disabled: 'Désactivée',
    protectedExtension: 'Protégée',
    checkedAt: 'Vérifié le {at}',
    neverChecked: 'Jamais vérifié',
    selected: 'aucune extension sélectionnée | une extension sélectionnée | {count} extensions sélectionnées',
    update: 'Mettre à jour',
    updateAll: 'Tout mettre à jour',
    searchPlaceholder: 'Filtrer par nom, identifiant ou type...',
    tableCaption: "Extensions installées, avec l'état des mises à jour et des vulnérabilités.",
    installed: 'Installée',
    securityBadge: 'Sécurité',
    unknownBadge: 'Inconnue',
    securityHint: "L'agent a signalé cette mise à jour comme une version de sécurité.",
    advisories: 'Vulnérabilités connues',
    protectedHint: 'Fournie avec le produit, ou non désinstallable.',
    enable: 'Activer',
    disable: 'Désactiver',
    selectOne: 'Sélectionner {name}',
    selectUpdatable: 'Sélectionner toutes celles à mettre à jour',
    updateSelected: 'Mettre à jour la sélection',
    updatableCount:
      'aucune mise à jour disponible | une extension à mettre à jour | {count} extensions à mettre à jour',
    noMatchDetail:
      "Aucune extension ne correspond à ce filtre. Choisissez « Toutes » pour revoir tout l'inventaire.",
    unknownCaveat:
      "{count} d'entre elles ne rapportent aucune version disponible. C'est inconnu, et non « à jour » : l'agent ne l'a pas dit, et {unknown} est la réponse honnête.",
    reauthUpdate:
      'Mettre à jour une extension sur {asset} | Mettre à jour {count} extensions sur {asset}',
    updating: "Mise à jour d'une extension | Mise à jour de {count} extensions",
    reauthEnable: 'Activer {name} sur {asset}',
    reauthDisable: 'Désactiver {name} sur {asset}',
    filters: {
      all: 'Toutes',
      outdated: 'Obsolètes',
      vulnerable: 'Vulnérables',
      disabled: 'Désactivées',
      updateUnknown: 'Mise à jour inconnue',
    },
    extension: 'Extension',
    noMatch: 'Aucune extension ne correspond à ce filtre',
    unknownHint: "Aucune version disponible n'a été rapportée. Personne ne le sait donc — ce n'est pas la même chose qu'« à jour ».",
    advisoriesCheckedAt: 'Vulnérabilités connues — dernière vérification le {at}',
  },

  charts: {
    // -- shared ------------------------------------------------------------------------------
    tableView: 'Voir le tableau',
    chartView: 'Voir le graphique',
    tableCaption: 'Données du graphique « {label} »',
    legend: 'Légende',
    noSample: 'aucune mesure',
    gap: 'Aucune mesure sur cet intervalle',
    total: 'Total',

    // -- honest empty states -----------------------------------------------------------------
    // The three reasons a chart can be blank are genuinely different facts, and the reader has to
    // be able to tell them apart. Collapsing them into one "no data" is the lie this whole
    // application exists not to tell.
    noDataYet: 'Aucune mesure pour le moment',
    probeWaiting: 'Sonde en attente du premier cycle',
    probeWaitingDetail:
      "Cette ressource est enregistrée, mais aucun cycle de sonde ne s'est encore terminé. Les premiers points apparaîtront ici dès la fin du prochain cycle.",
    noDataInWindow: 'Aucune mesure sur cette période',
    noDataInWindowDetail:
      'Rien n’a été mesuré entre {from} et {to}. Élargissez la période pour remonter plus loin.',
    cannotReport: 'Cette ressource ne peut pas rapporter cette mesure',
    cannotReportDetail:
      "Cette mesure vient d'un agent installé sur la ressource. Celle-ci est surveillée de l'extérieur : sa disponibilité et son temps de réponse sont mesurés, le reste ne l'est pas.",
    loadFailed: 'Échec du chargement des mesures',

    // -- uptime timeline ---------------------------------------------------------------------
    uptimeTimeline: 'Chronologie de disponibilité',
    uptimeTimelineSubtitle: 'Un intervalle par sonde, du plus ancien au plus récent',
    uptimeLegendUp: 'En ligne',
    uptimeLegendDown: 'Hors ligne',
    uptimeLegendUnknown: 'Aucune mesure',
    uptimeCell: '{at} — {state}',
    uptimeNoWindows: 'Aucun historique de disponibilité',
    uptimeNoWindowsDetail:
      "Aucune sonde n'a encore rendu de verdict sur cette période. Ce n'est pas 100% de disponibilité, et ce n'est pas 0%.",

    // -- availability meter ------------------------------------------------------------------
    availabilityMeter: 'Taux de disponibilité',
    availabilityUnknown: 'Taux inconnu',
    availabilityUnknownDetail:
      "Aucune sonde n'a encore mesuré cette ressource sur la période. Un taux inconnu n'est pas un taux de 100%.",
    outagesCount: 'aucune interruption | une interruption | {count} interruptions',

    // -- response time -----------------------------------------------------------------------
    responseTime: 'Temps de réponse',
    responseTimeSubtitle: 'Moyenne par intervalle, mesurée depuis les sondes',

    // -- activity volume ---------------------------------------------------------------------
    activityVolume: 'Volume d’activité',
    eventsPerBucket: 'Événements par intervalle',
    bucketsOf: 'intervalles de {bucket}',
    dimension: {
      category: 'catégorie',
      action: 'action',
      asset: 'ressource',
    },
    groupedBy: 'par {dimension}',
    eventCount: 'aucun événement | un événement | {count} événements',
    severeCount: '{count} de gravité élevée',
    noEventsInWindow: 'Aucun événement sur cette période',
    noEventsInWindowDetail:
      "Rien n'a été transmis entre {from} et {to}. Les événements arrivent depuis les agents installés sur les ressources ; une ressource sans agent n'en transmet aucun.",
    sharedScale: 'Échelle commune à tous les panneaux',
    othersFolded: 'Les catégories au-delà des huit premières sont regroupées sous « Autres ».',
  },

  metrics: {
    http_response_ms: 'Temps de réponse',
    http_up: 'Réponse de la sonde',
    http_status: 'Code HTTP',
    content_bytes: 'Taille de la réponse',
    dns_record_count: 'Enregistrements DNS',
    header_leak_count: 'En-têtes révélateurs',
    header_missing_count: 'En-têtes de sécurité manquants',
    port_open_count: 'Ports ouverts',
    port_unexpected_open_count: 'Ports ouverts inattendus',
    rdap_days_left: 'Expiration du domaine',
    tls_chain_length: 'Longueur de la chaîne TLS',
    tls_days_left: 'Expiration du certificat TLS',
    cpu_pct: 'Processeur',
    mem_pct: 'Mémoire',
    disk_pct: 'Disque',
    swap_pct: 'Swap',
    inode_pct: 'Inodes',
    load_1m: 'Charge (1 min)',
    net_rx_bytes: 'Réseau entrant',
    net_tx_bytes: 'Réseau sortant',
    proc_count: 'Processus',
  },

  pagination: {
    noResults: 'Aucun {label}',
    range: '{first}–{last} sur {total} {label}',
    rangeUnknown: '{label} {first}–{last}',
    page: 'Page {page} sur {pages}',
    first: 'Première page',
    previous: 'Page précédente',
    next: 'Page suivante',
    last: 'Dernière page',
    estimateHint: "Estimation du planificateur de requêtes, et non un décompte exact.",
    defaultLabel: 'résultats',
  },

  notFound: {
    title: 'Page introuvable',
    detail: "Cette adresse ne correspond à aucun écran de oui Observe.",
    back: 'Retour aux ressources',
  },

  /**
   * API failures.
   *
   * **Mapped on `error.code`, never on the English `detail`.** The `detail` string is prose written
   * by the server for an operator reading a log; it changes wording between releases, it is not
   * translated, and matching on it would produce an interface that silently reverts to English the
   * first time somebody improves a sentence in the core. `code` is the machine-readable statement
   * of what the application decided, it is an open enumeration on both sides of the protocol, and
   * it is the only thing here that is safe to branch on.
   *
   * `unrecognised` is what an unknown code renders as — the code itself is shown beside it, so a
   * core newer than this build still produces something a person can quote in a support thread.
   */
  errors: {
    archiveAsset: "Échec de l'archivage",
    saveAsset: "Échec de l'enregistrement de la ressource",
    mintToken: "Échec de la génération du jeton",
    UNAUTHORIZED: 'Votre session a expiré. Veuillez vous reconnecter.',
    FORBIDDEN: "Vous n'êtes pas autorisé à effectuer cette action.",
    MFA_REQUIRED: 'Un second facteur est requis pour continuer.',
    REAUTHENTICATION_REQUIRED:
      'Cette action est destructrice : confirmez votre identité pour la lancer.',
    CAPABILITY_UNSUPPORTED: 'Cette ressource ne déclare pas cette capacité.',
    CAPABILITY_DISABLED: 'Cette capacité est déclarée mais désactivée sur cette ressource.',
    MANIFEST_UNAVAILABLE:
      "Aucun manifeste n'a encore été récupéré : ce que cette ressource sait faire est inconnu.",
    ASSET_ARCHIVED: 'Cette ressource est archivée. Restaurez-la pour agir dessus.',
    ASSET_HAS_NO_AGENT:
      "Cette ressource n'a pas d'agent : elle est surveillée de l'extérieur et ne peut pas exécuter d'opération.",
    OP_CONFLICT: 'Une autre opération est déjà en cours sur cette ressource.',
    ILLEGAL_STATE: "L'état actuel ne permet pas cette action.",
    RATE_LIMITED: 'Trop de requêtes. Veuillez patienter avant de réessayer.',
    RATE_LIMITED_RETRY: 'Trop de requêtes. Veuillez réessayer dans {seconds} secondes.',
    INVALID_PARAMS: 'La requête est invalide. Vérifiez les champs signalés.',
    NOT_FOUND: 'Introuvable.',
    NOT_IMPLEMENTED: "Cette fonctionnalité n'est pas encore disponible dans le cœur.",
    INTERNAL: 'Le cœur a signalé une erreur interne.',
    UNAVAILABLE: 'Le service est temporairement indisponible.',

    /** Transport failures, which are a different piece of news from a refusal. */
    network: "Impossible de joindre le cœur de oui Observe. Vérifiez votre connexion réseau.",
    unrecognised: 'Le cœur a refusé la requête ({code}).',
    unknown: "Une erreur inattendue s'est produite.",

    /** Per-surface load failures, in the house frame « Échec de … ». */
    loadAssets: 'Échec du chargement des ressources',
    loadAsset: 'Échec du chargement de cette ressource',
    loadMetrics: 'Échec du chargement des mesures',
    loadUptime: 'Échec du chargement de la disponibilité',
    loadEvents: 'Échec de la recherche dans le journal',
    loadStatistics: 'Échec du chargement des statistiques',
    loadOperations: 'Échec du chargement des opérations',
    loadExtensions: "Échec du chargement de l'inventaire",
    loadAlerts: 'Échec du chargement des règles',
    loadManifest: 'Échec du chargement du manifeste',
  },
  ui: {
    progressLabel: "Progression de l'opération",
    progressUnknown: 'en cours, avancement inconnu',
    dismissToast: 'Masquer cette notification',
  },
} as const
